import type { LetterDraft, LetterEditorTab, LetterTemplateVariant } from "../letter-types"
import type {
  LetterAddressWindow,
  LetterPortalCopy,
  LetterSettingsPayload,
} from "../../../../../../../../../../../api/kehrblatt/letters"
import { resolveTemplateDefaults } from "../../../../../_shared/_logic/templates"
import { resolveLetterAddressWindow } from "./_logic/addressWindow"

const cloneValue = <T>(value: T): T => JSON.parse(JSON.stringify(value))
export const cloneDraft = (draft: LetterDraft): LetterDraft => cloneValue(draft)

export const readLetterDraft = (
  policy: OrganizationContactPolicy,
  useDefaults = false,
): LetterDraft => {
  const raw = (useDefaults ? policy.default_templates : policy.templates) as {
    letter: { portal: LetterPortalCopy }
  }
  const templates = resolveTemplateDefaults(raw)
  const personal = templates.letter.announcement.personal
  const reschedule = templates.letter.reschedule.personal
  const aushang = templates.letter.announcement.klebezettel
  const letterDefaults = policy.channel_defaults?.LETTER as Record<string, unknown> | undefined

  return cloneDraft({
    personal_letter_title_text: personal.title,
    personal_letter_subtitle_text: personal.subtitle,
    personal_letter_intro_text: personal.intro,
    personal_letter_portal_heading_text: personal.portal_heading,
    personal_letter_reschedule_title_text: reschedule.title,
    personal_letter_reschedule_subtitle_text: reschedule.subtitle,
    personal_letter_reschedule_intro_text: reschedule.intro,
    personal_letter_reschedule_portal_heading_text: reschedule.portal_heading,
    klebezettel_title_text: aushang.title,
    klebezettel_subtitle_text: aushang.subtitle,
    klebezettel_intro_text: aushang.intro,
    klebezettel_qr_heading_text: aushang.qr_heading,
    klebezettel_qr_body_text: aushang.qr_body,
    portal: raw.letter.portal,
    personal_letter_sender: policy.personal_letter_sender,
    letter_signature: (letterDefaults?.signature as OrganizationLetterSignature | null) ?? null,
    letter_address_window: resolveLetterAddressWindow(letterDefaults?.address_window),
  })
}

export const variantFields = (
  variant: LetterTemplateVariant,
): Exclude<
  keyof LetterDraft,
  "portal" | "personal_letter_sender" | "letter_signature" | "letter_address_window"
>[] =>
  variant === "aushang"
    ? [
        "klebezettel_title_text",
        "klebezettel_subtitle_text",
        "klebezettel_intro_text",
        "klebezettel_qr_heading_text",
        "klebezettel_qr_body_text",
      ]
    : variant === "reschedule"
      ? [
          "personal_letter_reschedule_title_text",
          "personal_letter_reschedule_subtitle_text",
          "personal_letter_reschedule_intro_text",
          "personal_letter_reschedule_portal_heading_text",
        ]
      : [
          "personal_letter_title_text",
          "personal_letter_subtitle_text",
          "personal_letter_intro_text",
          "personal_letter_portal_heading_text",
        ]

const commonFields = [
  "personal_letter_sender",
  "letter_signature",
  "letter_address_window",
  "portal",
] as const

export const defaultSettingsForVariant = (
  policy: OrganizationContactPolicy,
  variant: LetterTemplateVariant,
): LetterSettingsPayload | null => {
  const letter = policy.default_templates.letter
  const source =
    variant === "aushang" ? letter?.announcement?.klebezettel : letter?.[variant]?.personal
  const keys =
    variant === "aushang"
      ? ["title", "subtitle", "intro", "qr_heading", "qr_body"]
      : ["title", "subtitle", "intro", "portal_heading"]

  if (!source || keys.some((key) => typeof (source as Record<string, unknown>)[key] !== "string"))
    return null

  return Object.fromEntries(
    variantFields(variant).map((field, index) => [
      field,
      (source as Record<string, string>)[keys[index]],
    ]),
  )
}

export const settingsForTab = (draft: LetterDraft, tab: LetterEditorTab): LetterSettingsPayload =>
  Object.fromEntries(
    (tab === "other" ? commonFields : variantFields(tab)).map((key) => [
      key,
      key === "letter_address_window"
        ? (Object.fromEntries(
            Object.entries(draft.letter_address_window).map(([field, value]) => [
              field,
              Number(value),
            ]),
          ) as LetterAddressWindow)
        : draft[key],
    ]),
  )

export const changedSettings = (baseline: LetterDraft, draft: LetterDraft): LetterSettingsPayload =>
  Object.fromEntries(
    Object.entries(draft).filter(
      ([key, value]) =>
        JSON.stringify(value) !== JSON.stringify(baseline[key as keyof LetterDraft]),
    ),
  ) as LetterSettingsPayload

/** Apply the server snapshot locally; a second read must not turn a successful save into a failure. */
export const applySavedSettings = (
  policy: OrganizationContactPolicy,
  settings: LetterSettingsPayload,
): OrganizationContactPolicy => {
  const resolved = resolveTemplateDefaults(policy.templates)
  const templates = cloneValue({
    ...policy.templates,
    letter: {
      ...policy.templates.letter,
      announcement: {
        ...policy.templates.letter.announcement,
        ...resolved.letter.announcement,
      },
      reschedule: {
        ...policy.templates.letter.reschedule,
        ...resolved.letter.reschedule,
      },
      portal: readLetterDraft(policy).portal,
    },
  })
  const personal = templates.letter.announcement.personal
  const reschedule = templates.letter.reschedule.personal
  const aushang = templates.letter.announcement.klebezettel
  const assignments = [
    [personal, ["title", "subtitle", "intro", "portal_heading"], variantFields("announcement")],
    [reschedule, ["title", "subtitle", "intro", "portal_heading"], variantFields("reschedule")],
    [aushang, ["title", "subtitle", "intro", "qr_heading", "qr_body"], variantFields("aushang")],
  ] as const
  for (const [target, keys, fields] of assignments) {
    fields.forEach((field, index) => {
      if (typeof settings[field] === "string") {
        const writable = target as Record<string, string>
        writable[keys[index]] = settings[field] as string
      }
    })
  }

  if (settings.portal) templates.letter.portal = cloneValue(settings.portal)

  const letterDefaults = { ...((policy.channel_defaults?.LETTER ?? {}) as Record<string, unknown>) }

  if ("letter_signature" in settings) letterDefaults.signature = settings.letter_signature

  if (settings.letter_address_window) {
    letterDefaults.address_window = cloneValue(settings.letter_address_window)
  }

  return {
    ...policy,
    templates,
    personal_letter_sender: settings.personal_letter_sender ?? policy.personal_letter_sender,
    channel_defaults: { ...policy.channel_defaults, LETTER: letterDefaults },
  }
}
